import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
// Progress removed - not used in Review step (classification happens in Training)
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentUserCompanyId } from '@/lib/userUtils';
import { useToast } from '@/hooks/use-toast';
import { generateRuleFromTransaction } from '@/lib/bankTransactionRuleEngine';
import { type ClassificationResult } from '@/lib/transactionClassifier';
import { SubcategoryCombobox, DetailSourceCombobox } from './SubcategoryCombobox';
import { 
  Sparkles, 
  CheckCircle2, 
  Loader2,
  ArrowRight,
  Filter,
  CheckCheck,
  BarChart3,
  AlertTriangle,
  Save,
  TrendingUp,
  TrendingDown,
  CreditCard,
  ChevronDown,
  FileText
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface BankAccountsPhase2Step4Props {
  onNext: () => void;
  onPrevious: () => void;
  accountId: string | null;
}

interface Transaction {
  id: string;
  transaction_date: string;
  amount: number;
  description: string;
  merchant_name?: string;
  category_type?: 'revenue' | 'expenses' | 'balance_sheet' | null;
  subcategory?: string | null;
  detail_source?: string | null;
  location_id?: string;
  period_flag: string;
  classification_confidence?: number | null;
  classification_method?: string | null;
  auto_categorized?: boolean;
  needs_review?: boolean;
  account_id: string;
  account_name?: string; // Added for display
  is_credit_card?: boolean; // Flag for credit card accounts (inverted sign convention)
}

interface TransactionRow extends Transaction {
  editValues: {
    category_type: 'revenue' | 'expenses' | 'balance_sheet';
    subcategory: string;
    detailSource: string;
  };
  isSelected: boolean;
  suggestion?: ClassificationResult;
  isSaving?: boolean;
  isDirty?: boolean;
}

type FilterMode = 'all' | 'uncategorized' | 'categorized';
type ProcessingPhase = 'loading' | 'complete'; // Review step only loads, no classification

export const BankAccountsPhase2Step4 = ({ onNext, onPrevious, accountId }: BankAccountsPhase2Step4Props) => {
  const { toast } = useToast();
  const [transactions, setTransactions] = useState<TransactionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingPhase, setProcessingPhase] = useState<ProcessingPhase>('loading');
  // classificationProgress removed - classification happens in Training step, not Review
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [selectAll, setSelectAll] = useState(false);
  const [saving, setSaving] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 25;

  // Load transactions on mount and when accountId changes
  // No processedRef - we WANT to reload fresh data each time
  // This ensures newly categorized transactions appear in the review
  useEffect(() => {
    loadTransactionsForReview();
  }, [accountId]);

  const loadTransactionsForReview = async () => {
    try {
      setProcessingPhase('loading');
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;

      // Build query - if accountId is null, load from all accounts
      // Review step shows ALL training_window transactions (both categorized and uncategorized)
      // This allows users to review categorized transactions and also see skipped/uncategorized ones
      // Only select essential columns to reduce data transfer
      let query = supabase
        .from('bank_transactions')
        .select('id, transaction_date, amount, description, merchant_name, category_type, subcategory, detail_source, location_id, period_flag, account_id, auto_categorized, needs_review, rule_applied_id')
        .eq('company_id', companyId)
        .eq('period_flag', 'training_window');
      
      if (accountId) {
        query = query.eq('account_id', accountId);
      }

      const { data: transactionsData, error } = await query
        .order('transaction_date', { ascending: false });
      
      const categorizedCount = (transactionsData || []).filter((tx: any) => tx.category_type !== null).length;
      const uncategorizedCount = (transactionsData || []).filter((tx: any) => tx.category_type === null).length;
      console.log(`Review: Loaded ${transactionsData?.length || 0} training_window transactions (${categorizedCount} categorized, ${uncategorizedCount} uncategorized)`);

      if (error) throw error;

      // Load account details (name and type)
      const accountIds = [...new Set((transactionsData || []).map((tx: any) => tx.account_id))];
      const { data: accountsData } = await supabase
        .from('bank_accounts')
        .select('id, account_name, account_type')
        .in('id', accountIds);

      const accountMap = new Map((accountsData || []).map((acc: any) => [acc.id, { 
        name: acc.account_name, 
        type: acc.account_type,
        // Detect credit card by name or type
        isCreditCard: acc.account_type === 'credit' || 
                      acc.account_name?.toLowerCase().includes('credit') ||
                      acc.account_name?.toLowerCase().includes(' cc') ||
                      acc.account_name?.toLowerCase().endsWith(' cc')
      }]));

      // Add account names to transactions and sort by date
      // Review step does NOT run AI classification - that happens in Training step
      const txns: TransactionRow[] = (transactionsData || []).map((tx: any) => {
        const accountInfo = accountMap.get(tx.account_id) || { name: 'Unknown Account', type: null, isCreditCard: false };
        
        // For credit cards, the sign convention is INVERTED:
        // - Positive amounts on CC = Charges/Purchases = EXPENSES
        // - Negative amounts on CC = Payments/Credits = Balance Sheet (paying off debt)
        // For regular bank accounts:
        // - Positive amounts = Deposits/Revenue
        // - Negative amounts = Withdrawals/Expenses
        let defaultCategory: 'revenue' | 'expenses' | 'balance_sheet';
        if (accountInfo.isCreditCard) {
          // Credit card: positive = expense, negative = balance_sheet (debt payment)
          defaultCategory = tx.amount >= 0 ? 'expenses' : 'balance_sheet';
        } else {
          // Regular account: positive = revenue, negative = expense
          defaultCategory = tx.amount >= 0 ? 'revenue' : 'expenses';
        }
        
        return {
          ...tx,
          account_name: accountInfo.name,
          is_credit_card: accountInfo.isCreditCard,
          isSelected: false,
          isDirty: false,
          editValues: {
            category_type: tx.category_type || defaultCategory,
            subcategory: tx.subcategory || '',
            detailSource: tx.detail_source || '',
          },
        };
      });

      // Sort by date (most recent first)
      txns.sort((a, b) => 
        new Date(b.transaction_date).getTime() - new Date(a.transaction_date).getTime()
      );

      setTransactions(txns);
      setLoading(false);
      
      // Review step immediately completes loading - NO AI classification here
      // Classification should have already happened in the Training step
      setProcessingPhase('complete');

    } catch (error: any) {
      console.error('Error loading transactions:', error);
      toast({
        title: 'Error',
        description: 'Failed to load transactions',
        variant: 'destructive',
      });
      setLoading(false);
      setProcessingPhase('complete');
    }
  };

  // NOTE: AI classification has been removed from the Review step
  // Classification should happen in the Training step (Step 2)
  // This Review step only loads and displays transactions for user review/editing

  const handleEditValue = (id: string, field: 'category_type' | 'subcategory' | 'detailSource', value: string) => {
    setTransactions(prev => prev.map(tx => {
      if (tx.id === id) {
        let newEditValues = {
          ...tx.editValues,
          [field]: value,
        };
        
        // Reset dependent fields when category type changes
        if (field === 'category_type') {
          newEditValues = {
            ...newEditValues,
            subcategory: '',
            detailSource: '',
          };
        }
        // Reset detail source when subcategory changes
        if (field === 'subcategory') {
          newEditValues = {
            ...newEditValues,
            detailSource: '',
          };
        }
        
        const isDirty = 
          newEditValues.category_type !== (tx.category_type || (tx.amount >= 0 ? 'revenue' : 'expenses')) ||
          newEditValues.subcategory !== (tx.subcategory || '') ||
          newEditValues.detailSource !== (tx.detail_source || '');
        
        return {
          ...tx,
          editValues: newEditValues,
          isDirty,
        };
      }
      return tx;
    }));
  };

  const handleToggleSelect = (id: string) => {
    setTransactions(prev => prev.map(tx => 
      tx.id === id ? { ...tx, isSelected: !tx.isSelected } : tx
    ));
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    const visibleIds = new Set(paginatedTransactions.map(tx => tx.id));
    setTransactions(prev => prev.map(tx => 
      visibleIds.has(tx.id) ? { ...tx, isSelected: checked } : tx
    ));
  };

  const handleSaveTransaction = async (tx: TransactionRow) => {
    if (!tx.editValues.subcategory) {
      toast({
        title: 'Error',
        description: 'Subcategory is required',
        variant: 'destructive',
      });
      return;
    }

    setTransactions(prev => prev.map(t => 
      t.id === tx.id ? { ...t, isSaving: true } : t
    ));

    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId || !accountId) throw new Error('Missing IDs');

      await supabase
        .from('bank_transactions')
        .update({
          category_type: tx.editValues.category_type,
          subcategory: tx.editValues.subcategory,
          detail_source: tx.editValues.detailSource || null,
          auto_categorized: false,
          needs_review: false,
        })
        .eq('id', tx.id);

      await generateRuleFromTransaction(
        {
          id: tx.id,
          transaction_date: tx.transaction_date,
          amount: tx.amount,
          description: tx.description || '',
          merchant_name: tx.merchant_name,
          category_type: tx.editValues.category_type,
          subcategory: tx.editValues.subcategory,
          detail_source: tx.editValues.detailSource || undefined,
          location_id: tx.location_id || undefined,
        },
        companyId,
        tx.account_id
      );

      setTransactions(prev => prev.map(t => 
        t.id === tx.id ? {
          ...t,
          category_type: tx.editValues.category_type,
          subcategory: tx.editValues.subcategory,
          detail_source: tx.editValues.detailSource || null,
          isSaving: false,
          isDirty: false,
        } : t
      ));

      toast({
        title: 'Saved',
        description: 'Transaction updated',
      });
    } catch (error: any) {
      console.error('Error saving:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save',
        variant: 'destructive',
      });
      setTransactions(prev => prev.map(t => 
        t.id === tx.id ? { ...t, isSaving: false } : t
      ));
    }
  };

  const handleBatchSave = async () => {
    const selected = transactions.filter(
      tx => tx.isSelected && tx.editValues.subcategory
    );

    if (selected.length === 0) {
      toast({
        title: 'No valid selections',
        description: 'Select transactions with subcategory values to save',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    let savedCount = 0;

    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId || !accountId) throw new Error('Missing IDs');

      for (const tx of selected) {
        try {
          const updatePayload: Record<string, unknown> = {
            category_type: tx.editValues.category_type,
            subcategory: tx.editValues.subcategory,
            detail_source: tx.editValues.detailSource || null,
            auto_categorized: false,
            needs_review: false,
          };
          await supabase
            .from('bank_transactions')
            .update(updatePayload)
            .eq('id', tx.id);
          if (tx.editValues.subcategory === 'payroll') {
            await supabase
              .from('bank_transactions')
              .update({ payroll_assignment_status: 'unassigned' })
              .eq('id', tx.id)
              .neq('payroll_assignment_status', 'assigned');
          }

          try {
            await generateRuleFromTransaction(
              {
                id: tx.id,
                transaction_date: tx.transaction_date,
                amount: tx.amount,
                description: tx.description || '',
                merchant_name: tx.merchant_name,
                category_type: tx.editValues.category_type,
                subcategory: tx.editValues.subcategory,
                detail_source: tx.editValues.detailSource || undefined,
                location_id: tx.location_id || undefined,
              },
              companyId,
              tx.account_id
            );
          } catch (ruleErr) {
            console.warn('Onboarding: failed to create rule from transaction', tx.id, ruleErr);
          }

          savedCount++;
        } catch (error) {
          console.error(`Error saving ${tx.id}:`, error);
        }
      }

      setTransactions(prev => prev.map(tx => {
        if (selected.find(s => s.id === tx.id)) {
          return {
            ...tx,
            category_type: tx.editValues.category_type,
            subcategory: tx.editValues.subcategory,
            detail_source: tx.editValues.detailSource || null,
            isSelected: false,
            isDirty: false,
          };
        }
        return tx;
      }));

      toast({
        title: 'Batch Save Complete',
        description: `Updated ${savedCount} transactions`,
      });

      setSelectAll(false);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Batch save failed',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const filteredTransactions = useMemo(() => {
    switch (filterMode) {
      case 'uncategorized':
        return transactions.filter(tx => !tx.category_type);
      case 'categorized':
        return transactions.filter(tx => tx.category_type);
      default:
        return transactions;
    }
  }, [transactions, filterMode]);

  const paginatedTransactions = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredTransactions.slice(start, start + pageSize);
  }, [filteredTransactions, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredTransactions.length / pageSize);

  const stats = useMemo(() => {
    const total = transactions.length;
    const categorized = transactions.filter(tx => tx.category_type).length;
    const uncategorized = total - categorized;
    return { total, categorized, uncategorized };
  }, [transactions]);

  const selectedCount = transactions.filter(tx => tx.isSelected).length;
  const dirtyCount = transactions.filter(tx => tx.isDirty).length;

  const getConfidenceBadge = (confidence: number | null | undefined) => {
    if (!confidence) return <Badge variant="outline" className="text-xs whitespace-nowrap">Manual</Badge>;
    if (confidence >= 0.85) return <Badge className="bg-emerald-500 text-xs whitespace-nowrap">High {Math.round(confidence * 100)}%</Badge>;
    if (confidence >= 0.7) return <Badge className="bg-amber-500 text-xs whitespace-nowrap">Med {Math.round(confidence * 100)}%</Badge>;
    return <Badge variant="outline" className="text-xs whitespace-nowrap">Low {Math.round(confidence * 100)}%</Badge>;
  };

  if (loading && transactions.length === 0) {
    return (
      <div className="space-y-4 -mx-6">
        <div className="px-6 space-y-2">
          <Skeleton className="h-7 w-64" />
          <Skeleton className="h-4 w-96" />
        </div>
        <div className="px-6">
          <Card className="border-primary/30 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5">
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-6 w-6 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-64" />
                    <Skeleton className="h-4 w-32" />
                  </div>
                </div>
                <Skeleton className="h-2 w-full" />
              </div>
            </CardContent>
          </Card>
        </div>
        <div className="grid grid-cols-4 gap-3 px-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="border-l-4">
              <CardContent className="py-3 px-4 space-y-2">
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-6 w-12" />
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="w-full border-y">
          <div className="px-6 py-3 border-b bg-muted/50">
            <div className="grid grid-cols-9 gap-2">
              {Array.from({ length: 9 }).map((_, i) => (
                <Skeleton key={i} className="h-4 w-20" />
              ))}
            </div>
          </div>
          <div className="divide-y">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="grid grid-cols-9 gap-2 px-6 py-3 items-center">
                {Array.from({ length: 9 }).map((_, j) => (
                  <Skeleton key={j} className="h-8 w-full" />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 -mx-6">
      {/* Header */}
      <div className="px-6">
        <h3 className="text-xl font-semibold mb-1">Review All Transactions</h3>
        <p className="text-sm text-muted-foreground">
          Review and edit categorizations. Click on description to see full text.
        </p>
      </div>

      {/* Loading Progress */}
      {processingPhase === 'loading' && (
        <div className="px-6">
          <Card className="border-primary/30 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5">
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Loader2 className="h-6 w-6 text-primary animate-spin" />
                  <div className="flex-1">
                    <p className="font-medium">Loading transactions for review...</p>
                    <p className="text-sm text-muted-foreground">
                      This will show all transactions from your training
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Stats */}
      {processingPhase === 'complete' && (
        <div className="grid grid-cols-4 gap-3 px-6">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="py-3 px-4">
              <p className="text-xs text-muted-foreground uppercase">Total</p>
              <p className="text-xl font-bold">{stats.total}</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="py-3 px-4">
              <p className="text-xs text-muted-foreground uppercase">Categorized</p>
              <p className="text-xl font-bold text-emerald-600">{stats.categorized}</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="py-3 px-4">
              <p className="text-xs text-muted-foreground uppercase">Uncategorized</p>
              <p className="text-xl font-bold text-amber-600">{stats.uncategorized}</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="py-3 px-4">
              <p className="text-xs text-muted-foreground uppercase">Completion</p>
              <p className="text-xl font-bold text-purple-600">
                {stats.total > 0 ? Math.round((stats.categorized / stats.total) * 100) : 0}%
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Warning for uncategorized transactions */}
      {processingPhase === 'complete' && stats.uncategorized > 0 && (
        <div className="px-6">
          <Alert className="border-amber-500 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <strong>{stats.uncategorized} uncategorized transaction{stats.uncategorized !== 1 ? 's' : ''} found.</strong>
              {' '}These are transactions that were skipped during training. You can categorize them here or go back to the training step to categorize them.
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* Filters & Batch Actions */}
      {processingPhase === 'complete' && (
        <div className="flex flex-wrap items-center justify-between gap-4 bg-muted/30 px-6 py-3">
          <div className="flex items-center gap-3">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={filterMode} onValueChange={(v: FilterMode) => { setFilterMode(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-[180px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All ({stats.total})</SelectItem>
                <SelectItem value="uncategorized">Uncategorized ({stats.uncategorized})</SelectItem>
                <SelectItem value="categorized">Categorized ({stats.categorized})</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-3">
            {dirtyCount > 0 && (
              <span className="text-sm text-amber-600">
                {dirtyCount} unsaved
              </span>
            )}
            {selectedCount > 0 && (
              <Button size="sm" onClick={handleBatchSave} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCheck className="h-4 w-4 mr-2" />}
                Save Selected ({selectedCount})
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Empty State - No trained transactions yet */}
      {processingPhase === 'complete' && transactions.length === 0 && (
        <div className="px-6">
          <Card className="border-dashed border-2 border-muted-foreground/30">
            <CardContent className="py-12 text-center">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
              <h4 className="text-lg font-medium mb-2">No Trained Transactions Yet</h4>
              <p className="text-muted-foreground mb-4">
                Go back to the Training step to categorize your transactions first.
                <br />
                Once you categorize transactions, they will appear here for review.
              </p>
              <Button variant="outline" onClick={onPrevious}>
                ← Go Back to Training
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Full Width Transactions Table */}
      {processingPhase === 'complete' && filteredTransactions.length > 0 && (
        <div className="w-full overflow-x-auto border-y">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-10 px-3">
                  <Checkbox 
                    checked={selectAll} 
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
                <TableHead className="w-24 px-2">Account</TableHead>
                <TableHead className="w-20 px-2">Date</TableHead>
                <TableHead className="w-[180px] px-2">Description</TableHead>
                <TableHead className="w-24 px-2 text-right">Amount</TableHead>
                <TableHead className="w-32 px-2">Category Type</TableHead>
                <TableHead className="flex-1 min-w-[180px] px-2">Subcategory</TableHead>
                <TableHead className="flex-1 min-w-[180px] px-2">Detail</TableHead>
                <TableHead className="w-24 px-2">Confidence</TableHead>
                <TableHead className="w-14 px-2"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedTransactions.map((tx) => (
                <TableRow 
                  key={tx.id}
                  className={cn(
                    "group",
                    tx.isSelected && "bg-primary/5",
                    tx.isDirty && "bg-amber-50",
                    !tx.category_type && !tx.isDirty && "bg-slate-50/50",
                    tx.isSaving && "opacity-50"
                  )}
                >
                  <TableCell className="px-3">
                    <Checkbox 
                      checked={tx.isSelected} 
                      onCheckedChange={() => handleToggleSelect(tx.id)}
                    />
                  </TableCell>
                  <TableCell className="px-2 whitespace-nowrap">
                    <Badge 
                      variant="outline" 
                      className={cn(
                        "text-xs font-medium",
                        tx.account_name?.toLowerCase().includes('checking') && "bg-blue-50 border-blue-300 text-blue-700",
                        tx.account_name?.toLowerCase().includes('savings') && "bg-green-50 border-green-300 text-green-700",
                        (tx.account_name?.toLowerCase().includes('credit') || tx.account_name?.toLowerCase().includes('cc')) && "bg-purple-50 border-purple-300 text-purple-700",
                        tx.account_name?.toLowerCase().includes('business') && "bg-orange-50 border-orange-300 text-orange-700",
                        !tx.account_name?.toLowerCase().includes('checking') && 
                        !tx.account_name?.toLowerCase().includes('savings') && 
                        !tx.account_name?.toLowerCase().includes('credit') &&
                        !tx.account_name?.toLowerCase().includes('cc') &&
                        !tx.account_name?.toLowerCase().includes('business') && "bg-slate-50 border-slate-300 text-slate-700"
                      )}
                    >
                      {tx.account_name || 'Unknown'}
                    </Badge>
                  </TableCell>
                  <TableCell className="px-2 text-xs text-muted-foreground whitespace-nowrap">
                    {new Date(tx.transaction_date).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </TableCell>
                  <TableCell className="px-2">
                    <TooltipProvider>
                      <Tooltip>
                        <Popover>
                          <PopoverTrigger asChild>
                            <TooltipTrigger asChild>
                              <button className="flex items-center gap-1 text-left text-xs hover:text-primary transition-colors w-full">
                                <span className="truncate max-w-[140px]">
                                  {tx.description?.substring(0, 25)}{tx.description && tx.description.length > 25 ? '...' : ''}
                                </span>
                                <ChevronDown className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                              </button>
                            </TooltipTrigger>
                          </PopoverTrigger>
                          <PopoverContent side="bottom" align="start" className="w-[400px] p-3">
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <FileText className="h-3 w-3" />
                                Full Description
                              </div>
                              <p className="text-sm font-mono bg-muted p-2 rounded break-all">
                                {tx.description}
                              </p>
                            </div>
                          </PopoverContent>
                        </Popover>
                        <TooltipContent side="top" className="max-w-[350px]">
                          <p className="text-xs break-all">{tx.description}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell className="px-2 text-right font-mono text-sm">
                    {/* Color based on whether it's an expense or revenue */}
                    {/* Credit cards: positive = expense (red), negative = payment (green) */}
                    {/* Regular accounts: positive = revenue (green), negative = expense (red) */}
                    <span className={
                      tx.is_credit_card 
                        ? (tx.amount >= 0 ? 'text-red-600' : 'text-emerald-600')
                        : (tx.amount >= 0 ? 'text-emerald-600' : 'text-red-600')
                    }>
                      {tx.amount >= 0 ? '+' : ''}${Math.abs(tx.amount).toFixed(2)}
                    </span>
                  </TableCell>
                  <TableCell className="px-2">
                    <Select 
                      value={tx.editValues.category_type} 
                      onValueChange={(v) => handleEditValue(tx.id, 'category_type', v)}
                    >
                      <SelectTrigger className="h-8 text-xs w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="revenue">
                          <div className="flex items-center gap-2">
                            <TrendingUp className="h-3 w-3 text-emerald-600" />
                            Revenue
                          </div>
                        </SelectItem>
                        <SelectItem value="expenses">
                          <div className="flex items-center gap-2">
                            <TrendingDown className="h-3 w-3 text-red-600" />
                            Expenses
                          </div>
                        </SelectItem>
                        <SelectItem value="balance_sheet">
                          <div className="flex items-center gap-2">
                            <CreditCard className="h-3 w-3 text-blue-600" />
                            Balance Sheet
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="px-2">
                    <SubcategoryCombobox
                      categoryType={tx.editValues.category_type}
                      value={tx.editValues.subcategory}
                      onValueChange={(v) => handleEditValue(tx.id, 'subcategory', v)}
                      placeholder="Select..."
                      size="sm"
                    />
                  </TableCell>
                  <TableCell className="px-2">
                    <DetailSourceCombobox
                      subcategory={tx.editValues.subcategory}
                      value={tx.editValues.detailSource}
                      onValueChange={(v) => handleEditValue(tx.id, 'detailSource', v)}
                      placeholder="Select..."
                      size="sm"
                    />
                  </TableCell>
                  <TableCell className="px-2">
                    {getConfidenceBadge(tx.classification_confidence || tx.suggestion?.confidence)}
                  </TableCell>
                  <TableCell className="px-2">
                    {(tx.isDirty || !tx.category_type) && tx.editValues.subcategory && (
                      <Button
                        size="sm"
                        variant={tx.isDirty ? "default" : "outline"}
                        onClick={() => handleSaveTransaction(tx)}
                        disabled={tx.isSaving || !tx.editValues.subcategory}
                        className="h-7 w-7 p-0"
                      >
                        {tx.isSaving ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Save className="h-3 w-3" />
                        )}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6">
          <p className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, filteredTransactions.length)} of {filteredTransactions.length}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <span className="text-sm px-2">
              {currentPage} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-4 border-t px-6">
        <Button variant="outline" onClick={onPrevious}>
          ← Previous Step
        </Button>
        <Button onClick={onNext} disabled={processingPhase !== 'complete'}>
          Complete Setup
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};
